<template>
    <product-listings-section
        class="grocery-listings-section-module"
        :categories="categories"
        :listings-content="listingsContent"
        :selected-filters.sync="selectedFilters"
        :selected-sorts.sync="selectedSorts"
        :heading-text="attributes.heading_text"
        :hide-nav-items="attributes.hide_nav_items"
        :hide-breadcrumbs="attributes.hide_breadcrumbs"
        :hide-sidebar="attributes.hide_sidebar"
    />
</template>

<script>
    import ProductListingsSection from '../../../organisms/ProductListingsSection/ProductListingsSection';
    import { formatDietaryLabels } from '../../../../lib/dietary-labels';

    export default {
        name: 'grocery-listings-section-module',

        components: { ProductListingsSection },

        props: {
            attributes: {
                type: Object,
                required: true,
                default: () => ({}),
            },
        },

        async asyncData(context) {
            const {
                store,
                query,
                route,
                onlyFetchProducts = false,
            } = context;

            const selectedFilters = Array.isArray(query.filters)
                ? query.filters
                : [query.filters].filter(Boolean);

            const filters = {};

            if (selectedFilters.length) {
                filters.and = selectedFilters.map((key) => ({
                    dietary_labels: {
                        key: {
                            eq: key,
                        },
                    },
                }));
            }

            const selectedSorts = Array.isArray(query.sort)
                ? query.sort
                : [query.sort].filter(Boolean);

            const category = context.attributes.category?.documentId;

            if (category) {
                filters.or = [
                    {
                        category: {
                            documentId: {
                                eq: category,
                            },
                        },
                    },
                    {
                        categories: {
                            documentId: {
                                eq: category,
                            },
                        },
                    },
                ];
            }

            const locale = store.state.locales.locales.find(({ code }) => code === route.path.split('/')[1] ?? 'en')?.code ?? 'en';

            const data = await store.dispatch('grocery/fetchProducts', {
                filters,
                onlyFetchProducts,
                locale,
            });

            return {
                ...data,
                selectedFilters,
                selectedSorts,
            };
        },

        data() {
            return {
                selectedFilters: [
                    ...this.attributes.asyncData.selectedFilters,
                ],
                selectedSorts: [
                    ...this.attributes.asyncData.selectedSorts,
                ],
                products: [
                    ...this.attributes.asyncData.products,
                ],
            };
        },

        computed: {
            /**
             * Return the categories for the sidebar with an "all" item if we're on a category already.
             *
             * @returns {{text: string, to: string}[]}
             */
            categories() {
                const categories = [
                    ...this.attributes.asyncData.categories,
                ];

                if (this.attributes.category) {
                    categories.push({
                        text: this.$snippet('view_all', 'view all'),
                        to: this.attributes.grocery_link
                            ?.uri,
                    });
                }

                return categories;
            },

            /**
             * Return the breadcrumbs to display on the page.
             *
             * @returns {{text: string, to: string}[]}
             */
            breadcrumbs() {
                const breadcrumbs = [
                    {
                        text: this.$snippet('home', 'home'),
                        to: '/',
                    },
                    {
                        text: this.$snippet('grocery', 'grocery'),
                        to: this.attributes.grocery_link
                            ?.uri,
                    },
                ];

                const category = this.attributes.category;

                if (category) {
                    breadcrumbs.push({
                        text: category.name,
                        active: true,
                    });
                }

                return breadcrumbs;
            },

            /**
             * Return all the content to pass to the ProductListingsSection.
             *
             * @returns {{readMoreTitle: string, filterTitle: string, navItems: {name: string, to: string}[], filters: {image: {src: string, alt: string}, text: string, value: string|null}[], items: object[], breadcrumbs: {text: string, to: string}[], readMoreContent: string, categoryPrefix: string}}
             */
            listingsContent() {
                return {
                    breadcrumbs: this.breadcrumbs,
                    filterTitle: this.$snippet('filter_by', 'filter by'),
                    sortTitle: this.$snippet('sort_by', 'sort by'),
                    filters: formatDietaryLabels(this, this.attributes.dietary_labels),
                    sorts: formatDietaryLabels(this, this.attributes.sort_labels),
                    items: this.products,
                    navItems: this.attributes.asyncData.menuLinks,
                    readMoreButtonText: this.attributes.read_more?.button_text,
                    readMoreContent: this.attributes.read_more?.content,
                    categoryPrefix: this.attributes.category
                        ? this.$snippet('grocery_listing_page_prefix', 'grocery')
                        : '',
                };
            },
        },

        watch: {
            selectedFilters: 'fetchProducts',

            /**
             * Whenever the selected sorts change we want to update the products.
             */
            selectedSorts() {
                this.sortProducts(this.products);
                this.updateRoute();
            },

            /**
             * Whenever the query updates we want to update the internal value and refetch.
             *
             * This catches instances where the page is navigated back and the query is changed/removed.
             *
             * @param {string|Array} val
             */
            '$route.query.filters'(val) {
                if (!Array.isArray(val)) {
                    val = [val].filter(Boolean);
                }
                // Only update if the value has changed
                if (JSON.stringify(val) !== JSON.stringify(this.selectedFilters)) {
                    this.selectedFilters = val;
                }
            },

            /**
             * Whenever the query updates we want to update the internal value and refetch.
             *
             * This catches instances where the page is navigated back and the query is changed/removed.
             *
             * @param {string} val
             */
            '$route.query.sort'(val) {
                if (!Array.isArray(val)) {
                    val = [val].filter(Boolean);
                }
                // Only update if the value has changed
                if (JSON.stringify(val) !== JSON.stringify(this.selectedSorts)) {
                    this.selectedSorts = val;
                }
            },
        },

        methods: {
            /**
             * Fetch the products from the api and update the internal values.
             */
            async fetchProducts() {
                const { products } = await this.$options.asyncData({
                    store: this.$store,
                    query: {
                        filters: this.selectedFilters,
                    },
                    route: this.$route,
                    $config: this.$config,
                    attributes: this.attributes,
                    onlyFetchProducts: true,
                });

                this.sortProducts(products);

                this.updateRoute();

                this.products = products;
            },

            /**
             * Sort the products based on the selected sort.
             *
             * @param {object[]} products
             */
            sortProducts(products = []) {
                if (this.selectedSorts.length === 0) {
                    return;
                }

                const sortValue = this.selectedSorts[0].split(':')[0]; // sort will have :asc or :desc on the end

                products.sort((a, b) => {
                    return parseFloat(a[sortValue]) > parseFloat(b[sortValue]) ? -1 : 1;
                });
            },

            /**
             * Update the route with the selected filters and sorts.
             */
            updateRoute() {
                // Prevent duplicate navigation in tests
                if (
                    JSON.stringify(this.selectedFilters) !== JSON.stringify(this.$route.query.filters)
                    || JSON.stringify(this.selectedSorts) !== JSON.stringify(this.$route.query.sort)
                ) {
                    this.$router.push({
                        query: {
                            filters: this.selectedFilters,
                            sort: this.selectedSorts,
                        },
                    });
                }
            },
        },
    };
</script>
